import { Box, Button, Dialog, DialogActions, DialogContent, Grid, IconButton, LinearProgress, Skeleton, Theme, Typography, useTheme } from "@mui/material";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";
import { Form, Formik } from "formik";
import { useSnackbar } from "notistack";
import { DialogCloseIcon } from "../../assets/SvgIcons";
import { Item, ItemDetails } from "../ApplicationDetails";
import { initialDocument } from "./types";
import dayjs, { Dayjs } from "dayjs";
import { todaysdate } from "../../utils/utilityFunctions";
import { FileUpload } from "../investors/documentDetails";
import MFSubmitButton from "../../lib/formik/SubmitButton";
import { TextDatePicker } from "../onboarding/DatePickerWithTextFeild";


interface Values {
    validityDate: Dayjs;
    documents: initialDocument;
}

const initialValues: Values = {
    validityDate: dayjs(todaysdate()).add(1, 'year'),
    documents:
    {
        documentId: "",
        documentType: "arn",
        documentName: "",
        multipleFiles: "",
        required: 'true',
        options: [],
        uploadFormats: [
            "image/jpeg",
            "application/pdf",
            "image/png",
            "image/jpg"
        ],
        maxFiles: 1,
        maxFileSize: "2MB",
        minFileSize: "1KB",
        disabled: false,
        isActive: true,
        uniqueKey: ''
    }

};

export function ArnDialog({ isDialogOpen, handleDialogClose }: { isDialogOpen: boolean, handleDialogClose: () => void }): JSX.Element {
    if (!isDialogOpen) return <></>;

    const theme = useTheme();
    const [submitLoading, setSubmitLoading] = useState<boolean>(false);
    const [values, setValues] = useState<Values>(initialValues);
    const [documentUpload, setDocumentUpload] = useState<boolean>(false);

    const { enqueueSnackbar } = useSnackbar();

    const handleSubmit = async (values: Values) => {
        try {
            setSubmitLoading(true);

            enqueueSnackbar('Application updated successfully', { variant: 'success' });
            handleDialogClose();
        } catch (e) {
            enqueueSnackbar('Something went wrong, please try again', { variant: 'error' });
        } finally {
            setSubmitLoading(false);
        }
    }

    const handleDocumentUploadStart = () => {
        setDocumentUpload(true);
    };
    const handleDocumentUploadEnd = () => {
        setDocumentUpload(false);
    };

    return (
        <Dialog
            onClose={handleDialogClose}
            open={isDialogOpen}
            fullWidth
            sx={{
                '& .MuiPaper-root': {
                    borderRadius: '15px',
                    backgroundColor: 'background.default',
                    maxWidth: '900px',
                    padding: '25px',
                    m: 0,
                }
            }}
        >
            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h2" sx={{ fontWeight: 600 }}>
                        ARN Re Declare
                    </Typography>
                    <IconButton onClick={handleDialogClose}>
                        <DialogCloseIcon />
                    </IconButton>
                </Box>
                <Box sx={{ mt: 3 }}>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6}>
                            <Item sx={(theme: Theme) => ({
                                fontSize: '16px', lineHeight: '20px', letterSpacing: '0.02px', color: theme.palette.text.secondary, pb: '7px'
                            })}>ARN Number</Item>
                            <ItemDetails sx={(theme: Theme) => ({
                                color: theme.palette.text.primary, textTransform: 'capitalize', fontWeight: '500'
                            })}> 12345 </ItemDetails>
                        </Grid>
                    </Grid>
                    <Formik
                        initialValues={values}
                        // validationSchema={}
                        onSubmit={handleSubmit}
                    >
                        {({ values, setFieldValue }) => (
                            <Form >
                                <Grid container sx={{ display: 'flex', flexDirection: 'row', gap: '5px', justifyContent: 'space-between', mb:2 }}>
                                    <Grid
                                        item
                                        xs={12}
                                        lg={6}
                                        sx={{ '& .MuiGrid-item': { paddingTop: '0 !important' } }}
                                    >
                                        <TextDatePicker placeholder={"DD/MM/YYYY"} name={"validityDate"} />
                                    </Grid>
                                    <Grid
                                        item
                                        xs={12}
                                        lg={5}
                                    >
                                        <FileUpload
                                            onFileChange={(value, documentId, removeFile) => {
                                                const { file, ...rest } =
                                                    values.documents;
                                                setFieldValue(
                                                    `documents`,
                                                    {
                                                        ...rest,
                                                        documentId,
                                                        ...(!removeFile && { file }),
                                                    }
                                                );
                                            }}
                                            name={`documents.documentId`}
                                            documentData={values.documents}
                                            disabled={values.documents.disabled ?? false}
                                            onFileUpload={handleDocumentUploadStart}
                                            onFileUploaded={handleDocumentUploadEnd}
                                            fileUploadFormats={values.documents.uploadFormats}
                                            maxUploadFiles={values.documents.maxFiles}
                                            maxFileSize={values.documents.maxFileSize}
                                            minFileSize={values.documents.minFileSize}
                                            multipleFilesCheck={values.documents.multipleFiles}
                                        >
                                        </FileUpload>
                                    </Grid >
                                </Grid>

                                < DialogActions sx={{ width: '100%', mt: 1 }} >
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Button
                                                fullWidth
                                                variant="outlined"
                                                sx={(theme: Theme) => ({
                                                    color: 'primary.500',
                                                    backgroundColor: 'transparent',
                                                    borderColor: 'primary.500',
                                                    borderWidth: '0.5px',
                                                    borderStyle: 'solid',
                                                    padding: "10px",
                                                    "&:hover, &.Mui-focusVisible": {
                                                        borderColor: 'primary.600',
                                                        backgroundColor: "background.default"
                                                    },
                                                    ...theme.typography.body_600
                                                })}
                                                onClick={handleDialogClose}
                                                disabled={submitLoading}
                                            >
                                                Cancel
                                            </Button>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <MFSubmitButton
                                                disabled={submitLoading}
                                                loadingEnable={submitLoading}
                                                label={'Submit & Upload'}
                                            />
                                        </Grid>
                                    </Grid>
                                </DialogActions>
                            </Form>
                        )}
                    </Formik>
                </Box>
            </DialogContent>
        </Dialog >
    );
}
